import '~/utils/server-localstorage-guard';
import type { CSSProperties, MantineColorScheme } from '@mantine/core';
import {
  AppShell,
  Button,
  Drawer,
  Fieldset,
  MantineProvider,
  Modal,
  Portal,
  RadioCard,
  Tabs,
  createTheme,
  rem,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { Provider, useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { AppProps } from 'next/app';
import { type AppType } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { Header } from '~/components/common/Header';
import { OutageBanner } from '~/components/common/OutageBanner';
import { Sidebar } from '~/components/common/Sidebar';
import { env } from '~/env.mjs';
import { api } from '~/trpc-extra/api';
import { DailyProvider } from '@daily-co/daily-react';
import { CodeHighlightAdapterProvider, createHighlightJsAdapter } from '@mantine/code-highlight';
import { useHotkeys } from '@mantine/hooks';
import { ModalsProvider } from '@mantine/modals';
import hljs from 'highlight.js/lib/core';
import bashLang from 'highlight.js/lib/languages/bash';
import tsLang from 'highlight.js/lib/languages/typescript';
import yamlLang from 'highlight.js/lib/languages/yaml';
import Head from 'next/head';
import { adminUiHiddenAtom, hideNavbarAtom, navbarOpenAtom, onTestPageAtom } from '~/atoms/atoms';
import { useViewModeValue } from '~/hooks/context/useViewMode';
import StablyRequiredAuthProvider from '~/components/auth/AuthProvider';
import { AuthenticatedBootstrapGate } from '~/components/auth/AuthenticatedBootstrapGate';
import { PaymentRequiredWrapper } from '~/components/auth/PaymentRequiredWrapper';
import { getViewModeFromPath, useViewModeSync } from '~/hooks/context/useViewModeSync';
import { HyperDXProvider } from '~/components/observability/HyperDXProvider';
import { useShowNewFeatures } from '~/hooks/new-features/use-show-new-features';
import { useCommandMenu } from '~/hooks/search/useCommandMenu';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useAutoSwitchOrg } from '~/hooks/auth/use-auto-switch-org';
import { useCheckForUpdates } from '~/hooks/use-check-for-updates';
import { applyGoogleTranslateWorkaround } from '~/utils/googleTranslateWorkaround';

// css imports
// !!!! Order matters !!!!
// Mantine CSS is imported here (unlayered) BEFORE globals.css.
// In globals.css, Tailwind preflight is in @layer base (lower priority than Mantine),
// but Tailwind utilities are unlayered and come after — so they override Mantine.
import '@mantine/carousel/styles.css';
import '@mantine/core/styles.css';
import '@mantine/code-highlight/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/tiptap/styles.css';
import '~/styles/globals.css';

applyGoogleTranslateWorkaround();

hljs.registerLanguage('typescript', tsLang);
hljs.registerLanguage('yaml', yamlLang);
hljs.registerLanguage('bash', bashLang);

const theme = createTheme({
  cursorType: 'pointer',
  defaultGradient: { from: 'indigo', to: 'cyan' },
  components: {
    // https://mantine.dev/changelog/7-17-0/#portal-reusetargetnode-prop
    Portal: Portal.extend({
      defaultProps: {
        reuseTargetNode: true,
      },
    }),
    Button: Button.extend({
      defaultProps: {
        radius: 'md',
        styles: {
          root: {
            '--button-padding-x': rem(10),
            '--button-height': rem(32),
          },
        },
        classNames: {
          section: 'm-1',
        },
      },
    }),
    // Giving some nicer default around the highlighting (seleceted/hovered), and disabled behavior
    RadioCard: RadioCard.extend({
      defaultProps: {
        className:
          'relative p-[var(--mantine-spacing-md)] transition-[border-color] duration-150 ease-out hover:bg-[var(--mantine-color-gray-0)] disabled:cursor-not-allowed disabled:opacity-60  data-[checked]:border-[var(--mantine-primary-color-filled)] dark:hover:bg-[var(--mantine-color-dark-6)]',
      },
    }),
    Tabs: Tabs.extend({
      defaultProps: {
        styles: {
          tabLabel: {
            fontWeight: 500, // medium weight
            letterSpacing: '-0.01rem', // tighter horizontal spacing
          },
        },
        classNames: {
          tab: '[&:not([data-active])]:opacity-70 [&:not([data-active])]:hover:opacity-100 px-1 mx-[5px]',
        },
      },
    }),
    Drawer: Drawer.extend({
      defaultProps: {
        overlayProps: {
          backgroundOpacity: 0.15,
          blur: 0.5,
        },
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        radius: 'md',
        overlayProps: {
          backgroundOpacity: 0.25,
          blur: 0,
        },
        shadow: 'xl',
      },
    }),
    Fieldset: Fieldset.extend({
      defaultProps: {
        classNames: {
          legend: 'mb-2',
        },
      },
    }),
  },
  fontSizes: {
    xs: rem(11),
    sm: rem(13),
    md: rem(15),
    lg: rem(17),
    xl: rem(21),
  },
});

const PUBLIC_ROUTES = ['/lite', '/utils'];

// Routes that need auth but should render without the app shell (no sidebar/header)
// Used for OAuth redirect pages that open in new tabs
const MINIMAL_SHELL_ROUTES = ['/integrations/gitlab/redirect', '/integrations/github/redirect'];

// Note: Styles are tricky since we use Tailwind and Mantine
// To have them work together, we disable tailwind preflight (in config)
const MyApp = ({ Component, pageProps, router }: AppProps & { colorScheme: MantineColorScheme }) => {
  // Since redirect are super akward while using Vercel's name server, we have this for homepage re-routing
  useEffect(() => {
    const host = window.location.host.toLocaleLowerCase();
    if (host === 'www.stably.dev' || host === 'stably.dev') {
      window.location.replace('http://www.stably.ai');
    }
  }, []);

  const isPublicRoute = PUBLIC_ROUTES.some((route) => router.pathname.startsWith(route));
  const isMinimalShellRoute = MINIMAL_SHELL_ROUTES.some((route) => router.pathname.startsWith(route));

  const highlightJsAdapter = createHighlightJsAdapter(hljs);

  useCheckForUpdates();

  return (
    <DailyProvider>
      <MantineProvider
        theme={theme}
        defaultColorScheme="auto"
      >
        <CodeHighlightAdapterProvider adapter={highlightJsAdapter}>
          <Provider>
            {isPublicRoute ?
              <ModalsProvider>
                <Notifications />
                <Component {...pageProps} />
              </ModalsProvider>
            : isMinimalShellRoute ?
              // Minimal shell routes: need auth but no sidebar/header (e.g., OAuth redirect pages)
              <StablyRequiredAuthProvider>
                <ModalsProvider>
                  <Notifications />
                  <AuthenticatedBootstrapGate>
                    <Component {...pageProps} />
                  </AuthenticatedBootstrapGate>
                </ModalsProvider>
              </StablyRequiredAuthProvider>
            : <StablyRequiredAuthProvider>
                <ModalsProvider>
                  <Notifications />
                  {/* Keep zero-org bootstrap ahead of billing and route render.
                      Several routes assume activeOrg exists and will crash if they mount first. */}
                  <AuthenticatedBootstrapGate>
                    <PaymentRequiredWrapper>
                      <InnerMostApp
                        pageProps={pageProps}
                        Component={Component}
                        router={router}
                      />
                    </PaymentRequiredWrapper>
                  </AuthenticatedBootstrapGate>
                </ModalsProvider>
              </StablyRequiredAuthProvider>
            }
          </Provider>
        </CodeHighlightAdapterProvider>
      </MantineProvider>
    </DailyProvider>
  );
};

// Outage banner controlled via environment variable
const ENABLE_OUTAGE_BANNER = !!env.NEXT_PUBLIC_OUTAGE_BANNER_MESSAGE;
export const OUTAGE_BANNER_HEIGHT = ENABLE_OUTAGE_BANNER ? 36 : 0;

const mainScrollAdjustedStyles: CSSProperties = {
  marginTop: 'var(--app-shell-header-offset)',
  paddingTop: 16,
  overflowY: 'auto',
  height: `calc(100vh - var(--app-shell-header-offset) - ${OUTAGE_BANNER_HEIGHT}px)`,
  minHeight: 'unset',
};

// This way we have themeing here
const InnerMostApp: AppType = ({ Component, pageProps }) => {
  const { toggleColorScheme } = useMantineColorScheme();
  const colorScheme = useComputedColorScheme();
  const { toggleCommandMenu } = useCommandMenu();
  const setAdminUiHidden = useSetAtom(adminUiHiddenAtom);
  const router = useRouter();
  const rawProjectId = router.query.projectId;
  const projectId = typeof rawProjectId === 'string' ? rawProjectId : undefined;
  const { orgMetadata } = useAuthData();
  const isAgentLockedOrg = orgMetadata?.defaultViewMode === 'agent';

  useHotkeys([
    ['meta+shift+l', toggleColorScheme],
    ['ctrl+shift+l', toggleColorScheme],
    ['mod+k', toggleCommandMenu],
    ['meta+shift+0', () => setAdminUiHidden((x) => !x)],
    ['ctrl+shift+0', () => setAdminUiHidden((x) => !x)],
  ]);

  useAutoSwitchOrg(projectId);
  useShowNewFeatures();
  useViewModeSync();

  const { viewMode: detectedRouteViewMode, isHomepage } = getViewModeFromPath(router.pathname);
  const shouldBlockClassicRoute =
    !!projectId && isAgentLockedOrg && (isHomepage || detectedRouteViewMode === 'classic');

  const [navbarOpen, setNavbarOpen] = useAtom(navbarOpenAtom);
  const hideNavbar = useAtomValue(hideNavbarAtom);
  const onTestPage = useAtomValue(onTestPageAtom);
  const viewMode = useViewModeValue(projectId);

  // Use expanded sidebar for all views including Agent 2.0
  const isCollapsedSidebar = false;

  // Hide navbar on the agent session detail page (full-screen experience), but keep it on the agents list page
  const onAgentsPage = viewMode === 'agent' && router.pathname === '/project/[projectId]/agents/[agentSessionId]';
  const onAdminPage = router.pathname.startsWith('/admin') || router.pathname.includes('/admin/');
  const onSettingsPage = router.pathname.startsWith('/project/[projectId]/settings');

  const highlightJsThemeUrl =
    colorScheme === 'dark' ?
      'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.11.1/styles/a11y-dark.min.css'
    : 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.11.1/styles/atom-one-light.min.css';

  useEffect(() => {
    if (hideNavbar || onTestPage || onAgentsPage || onAdminPage) {
      setNavbarOpen(false);
    } else if (!hideNavbar && !onTestPage && !onAgentsPage && !onAdminPage) {
      // Restore navbar when leaving agents/test page (unless user manually hid it)
      setNavbarOpen(true);
    }
  }, [hideNavbar, onTestPage, onAgentsPage, onAdminPage, setNavbarOpen]);

  useEffect(() => {
    if (!shouldBlockClassicRoute || !projectId) {
      return;
    }
    router.replace(`/project/${projectId}/home`).catch(console.error);
  }, [shouldBlockClassicRoute, projectId, router]);

  if (shouldBlockClassicRoute) {
    return <div className="flex h-screen items-center justify-center text-sm text-gray-500">Redirecting...</div>;
  }

  return (
    <>
      <Head>
        <link
          key="highlight-theme"
          rel="stylesheet"
          href={highlightJsThemeUrl}
        />
      </Head>
      {ENABLE_OUTAGE_BANNER ?
        <OutageBanner />
      : null}
      <AppShell
        layout="alt"
        styles={{
          header: {
            padding: 0,
            top: `${OUTAGE_BANNER_HEIGHT}px`,
          },
          navbar: {
            top: `${OUTAGE_BANNER_HEIGHT}px`,
            height: `calc(100vh - ${OUTAGE_BANNER_HEIGHT}px)`,
          },
          main:
            !onTestPage && !onAgentsPage ?
              {
                ...mainScrollAdjustedStyles,
                marginTop: `calc(var(--app-shell-header-offset) + ${OUTAGE_BANNER_HEIGHT}px)`,
                ...(onSettingsPage && { paddingTop: 0 }),
              }
            : {
                height: `calc(100vh - ${OUTAGE_BANNER_HEIGHT}px)`,
                paddingTop: `${OUTAGE_BANNER_HEIGHT}px`,
              },
        }}
        // The test edit page doesn't have a navbar and requires a 0px x-padding
        padding={onTestPage || onSettingsPage || onAgentsPage ? 0 : 'md'}
        header={{ height: 60, collapsed: onTestPage || viewMode === 'agent' }}
        navbar={{
          width: isCollapsedSidebar ? 56 : 220,
          breakpoint: 'xs',
          collapsed: {
            desktop: !navbarOpen,
            mobile: true,
          },
        }}
      >
        <HyperDXProvider />
        <AppShell.Header className="px-6">
          <Header />
        </AppShell.Header>
        <AppShell.Navbar p={isCollapsedSidebar ? 4 : 4}>
          <Sidebar isCollapsed={isCollapsedSidebar} />
        </AppShell.Navbar>
        {/* h-screen is needed here for children to properly be able to be h-full */}
        <AppShell.Main>
          <Component {...pageProps} />
        </AppShell.Main>
      </AppShell>
    </>
  );
};

export default api.withTRPC(MyApp);
